import React, { useState, useEffect } from "react";
import { supabase } from "./supabase";
import { logSupabaseError } from "./utils";
import StaffManager from "./StaffManager";
import "./App.css";

// ── Reusable Toggle Row ───────────────────────────────────────────────────────
function ToggleRow({ label, description, value, onChange, isLast = false }) {
    const isOn = value !== "false";
    return (
        <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "12px 0",
            borderBottom: isLast ? "none" : "1px solid var(--border)",
        }}>
            <div>
                <p style={{ fontSize: "14px", fontWeight: 600, color: "var(--text)", margin: 0 }}>{label}</p>
                <p style={{ fontSize: "12px", color: "var(--text3)", marginTop: "2px", marginBottom: 0 }}>{description}</p>
            </div>
            <button
                onClick={onChange}
                aria-label={`Toggle ${label}`}
                style={{
                    width: "48px", height: "26px", borderRadius: "13px",
                    background: isOn ? "var(--cyan)" : "var(--border2)",
                    border: "none", cursor: "pointer", position: "relative",
                    transition: "background 0.2s", flexShrink: 0,
                }}
            >
                <div style={{
                    position: "absolute", top: "3px",
                    left: isOn ? "25px" : "3px",
                    width: "20px", height: "20px", borderRadius: "50%",
                    background: "white", transition: "left 0.2s",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
                }} />
            </button>
        </div>
    );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function Settings({ onClose, currentUser, onSignOut }) {
    const [settings, setSettings] = useState({
        vat_enabled: "false",
        vat_rate: "15",
        theme: "dark",
        store_name: "",
        store_tin: "",
        store_address: "",
        store_phone: "",
        receipt_print_enabled: "true",
        my_sales_enabled: "true",
        camera_scanner_enabled: "true",
        alerts_enabled: "true",
        discount_enabled: "false",
        mobile_pos_enabled: "true",
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(false);
    const [savedTheme, setSavedTheme] = useState("dark");

    useEffect(() => { fetchSettings(); }, []);

    async function fetchSettings() {
        const { data, error } = await supabase
            .from("store_settings")
            .select("key, value");
        logSupabaseError("Settings: fetchSettings", error);
        if (data) {
            const map = {};
            data.forEach((s) => { map[s.key] = s.value; });
            setSettings((prev) => ({ ...prev, ...map }));
            setSavedTheme(map.theme ?? "dark");
        }
        setLoading(false);
    }

    async function handleSave() {
        setSaving(true);

        const { data: profile, error: profileError } = await supabase
            .from("profiles")
            .select("store_id")
            .eq("id", currentUser.id)
            .single();
        logSupabaseError("Settings: load profile store_id", profileError);

        const store_id = profile?.store_id;
        if (!store_id) { setSaving(false); return; }

        const entries = Object.entries(settings);
        const succeeded = [];
        const failed = [];
        for (const [key, value] of entries) {
            const { error } = await supabase
                .from("store_settings")
                .upsert(
                    { key, value, store_id, updated_at: new Date().toISOString() },
                    { onConflict: "key,store_id" }
                );
            if (error) {
                failed.push({ key, message: error.message });
            } else {
                succeeded.push(key);
            }
        }
        setSaving(false);

        if (failed.length === 0) {
            setSuccess(true);
            setSavedTheme(settings.theme);
            setTimeout(() => setSuccess(false), 3000);
            return;
        }

        // savedTheme should only reflect what actually persisted — if "theme"
        // itself failed, don't claim it saved.
        if (succeeded.includes("theme")) {
            setSavedTheme(settings.theme);
        }

        const failedLines = failed
            .map((f) => `❌ ${f.key}: ${f.message}`)
            .join("\n");
        const succeededLine = succeeded.length > 0
            ? `\n\n✅ Saved: ${succeeded.join(", ")}`
            : "";
        alert(`Some settings failed to save:\n\n${failedLines}${succeededLine}`);
    }

    function toggle(key) {
        setSettings((prev) => ({
            ...prev,
            [key]: prev[key] === "false" ? "true" : "false",
        }));
    }

    function applyThemePreview(t) {
        const root = document.documentElement;
        if (t === "light") {
            root.style.setProperty("--bg", "#F5F5F7");
            root.style.setProperty("--surface", "#FFFFFF");
            root.style.setProperty("--elevated", "#F0F0F2");
            root.style.setProperty("--card", "#E8E8EA");
            root.style.setProperty("--border", "#D1D1D6");
            root.style.setProperty("--border2", "#C7C7CC");
            root.style.setProperty("--text", "#1C1C1E");
            root.style.setProperty("--text2", "#3A3A3C");
            root.style.setProperty("--text3", "#6C6C70");
        } else {
            root.style.setProperty("--bg", "#0E0E10");
            root.style.setProperty("--surface", "#161618");
            root.style.setProperty("--elevated", "#1C1C1E");
            root.style.setProperty("--card", "#242426");
            root.style.setProperty("--border", "#2C2C2E");
            root.style.setProperty("--border2", "#3A3A3C");
            root.style.setProperty("--text", "#F5F5F7");
            root.style.setProperty("--text2", "#98989F");
            root.style.setProperty("--text3", "#5A5A60");
        }
    }

    const inputStyle = {
        height: "38px", padding: "0 12px",
        background: "var(--elevated)", border: "1px solid var(--border)",
        borderRadius: "var(--radius)", color: "var(--text)",
        fontFamily: "var(--font)", outline: "none", fontSize: "13px", width: "100%",
        transition: "border 0.15s", boxSizing: "border-box",
    };

    return (
        <div className="report-overlay">
            <div className="report-panel" style={{ maxWidth: "620px" }}>

                {/* ── Header ── */}
                <div className="report-header">
                    <div>
                        <h2 className="report-title">⚙️ Store Settings</h2>
                        <p className="report-date">Store info, appearance, system toggles & staff</p>
                    </div>
                    <button
                        className="report-close"
                        onClick={() => {
                            applyThemePreview(savedTheme ?? "dark");
                            onClose();
                        }}
                    >✕</button>
                </div>

                {loading ? (
                    <p className="report-loading">Loading settings...</p>
                ) : (
                    <div style={{
                        flex: 1, minHeight: 0, overflowY: "auto", padding: "20px",
                        display: "flex", flexDirection: "column", gap: "16px",
                    }}>

                        {/* ── Save success banner ── */}
                        {success && (
                            <div style={{
                                padding: "10px 14px", background: "var(--green-dim)",
                                border: "1px solid rgba(50,215,75,0.3)", borderRadius: "var(--radius)",
                                color: "var(--green)", fontSize: "13px",
                            }}>
                                ✅ Settings saved successfully!
                            </div>
                        )}

                        {/* ── 1. Store Information ── */}
                        <div className="wh-section">
                            <p className="wh-section-title">🏪 Store Information</p>
                            <div className="settings-store-info-row">
                                <div className="inv-form-field" style={{ gridColumn: "span 2" }}>
                                    <label>Store Name</label>
                                    <input
                                        value={settings.store_name}
                                        onChange={(e) => setSettings({ ...settings, store_name: e.target.value })}
                                        placeholder="e.g. Guadaye Mini Market"
                                        style={inputStyle}
                                    />
                                </div>
                                <div className="inv-form-field">
                                    <label>Store TIN</label>
                                    <input
                                        value={settings.store_tin}
                                        onChange={(e) => setSettings({ ...settings, store_tin: e.target.value })}
                                        placeholder="e.g. 0012345678"
                                        style={inputStyle}
                                    />
                                </div>
                                <div className="inv-form-field">
                                    <label>Phone</label>
                                    <input
                                        value={settings.store_phone}
                                        onChange={(e) => setSettings({ ...settings, store_phone: e.target.value })}
                                        placeholder="+251 9xx xxx xxx"
                                        style={inputStyle}
                                    />
                                </div>
                                <div className="inv-form-field" style={{ gridColumn: "span 2" }}>
                                    <label>Address</label>
                                    <input
                                        value={settings.store_address}
                                        onChange={(e) => setSettings({ ...settings, store_address: e.target.value })}
                                        placeholder="e.g. Addis Ababa, Bole"
                                        style={inputStyle}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* ── 2. Appearance ── */}
                        <div className="wh-section">
                            <p className="wh-section-title">🎨 Appearance</p>
                            <div style={{
                                display: "flex", alignItems: "center",
                                justifyContent: "space-between", padding: "12px 0",
                            }}>
                                <div>
                                    <p style={{ fontSize: "14px", fontWeight: 600, color: "var(--text)", margin: 0 }}>Theme</p>
                                    <p style={{ fontSize: "12px", color: "var(--text3)", marginTop: "2px", marginBottom: 0 }}>
                                        Switch between dark and light mode
                                    </p>
                                </div>
                                <div style={{ display: "flex", gap: "6px" }}>
                                    {["dark", "light"].map((t) => (
                                        <button
                                            key={t}
                                            onClick={() => {
                                                setSettings({ ...settings, theme: t });
                                                applyThemePreview(t);
                                            }}
                                            style={{
                                                height: "36px", padding: "0 20px",
                                                borderRadius: "var(--radius)",
                                                border: `1px solid ${settings.theme === t ? "var(--cyan)" : "var(--border)"}`,
                                                background: settings.theme === t ? "var(--cyan-dim)" : "var(--elevated)",
                                                color: settings.theme === t ? "var(--cyan)" : "var(--text2)",
                                                fontWeight: settings.theme === t ? 700 : 400,
                                                cursor: "pointer", fontSize: "13px",
                                                fontFamily: "var(--font)", transition: "all 0.15s",
                                            }}
                                        >
                                            {t === "dark" ? "🌙 " : "☀️ "}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* ── 3. System Settings ── */}
                        <div className="wh-section">
                            <p className="wh-section-title">🔧 System Settings</p>

                            {/* VAT subsection */}
                            <p style={{
                                fontSize: "11px", fontWeight: 700, color: "var(--text3)",
                                textTransform: "uppercase", letterSpacing: "0.6px",
                                margin: "4px 0 0 0",
                            }}>VAT</p>

                            <ToggleRow
                                label="Enable VAT"
                                description="Add VAT to all sales transactions and receipts"
                                value={settings.vat_enabled}
                                onChange={() => toggle("vat_enabled")}
                            />

                            {settings.vat_enabled === "true" && (
                                <div className="inv-form-field" style={{ paddingBottom: "12px", borderBottom: "1px solid var(--border)" }}>
                                    <label>VAT Rate (%)</label>
                                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                        <input
                                            type="number" min="0" max="100"
                                            value={settings.vat_rate}
                                            onChange={(e) => setSettings({ ...settings, vat_rate: e.target.value })}
                                            style={{
                                                ...inputStyle, width: "100px", textAlign: "center",
                                                fontFamily: "var(--mono)", fontSize: "18px", fontWeight: 700,
                                            }}
                                        />
                                        <div>
                                            <p style={{ fontSize: "13px", color: "var(--text2)", margin: 0 }}>
                                                On a ETB 100 sale → ETB {Number(settings.vat_rate).toFixed(2)} VAT
                                            </p>
                                            <p style={{ fontSize: "11px", color: "var(--text3)", marginTop: "2px", marginBottom: 0 }}>
                                                Total charged: ETB {(100 + Number(settings.vat_rate)).toFixed(2)}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* POS Features subsection */}
                            <p style={{
                                fontSize: "11px", fontWeight: 700, color: "var(--text3)",
                                textTransform: "uppercase", letterSpacing: "0.6px",
                                margin: "16px 0 0 0",
                            }}>POS Features</p>

                            <ToggleRow
                                label="Receipt Printing"
                                description="Allow cashiers to print receipts after payment"
                                value={settings.receipt_print_enabled}
                                onChange={() => toggle("receipt_print_enabled")}
                            />
                            <ToggleRow
                                label="Cashier Sales Report"
                                description="Allow cashiers to view their own daily sales"
                                value={settings.my_sales_enabled}
                                onChange={() => toggle("my_sales_enabled")}
                            />
                            <ToggleRow
                                label="Cashier Discounts"
                                description="Allow cashiers to apply discounts at POS"
                                value={settings.discount_enabled}
                                onChange={() => toggle("discount_enabled")}
                            />
                            <ToggleRow
                                label="Enable full POS on mobile devices"
                                description="Off blocks the full desktop layout on phones/small tablets with a message, until a mobile-friendly design ships"
                                value={settings.mobile_pos_enabled}
                                onChange={() => toggle("mobile_pos_enabled")}
                            />

                            {/* Hardware subsection */}
                            <p style={{
                                fontSize: "11px", fontWeight: 700, color: "var(--text3)",
                                textTransform: "uppercase", letterSpacing: "0.6px",
                                margin: "16px 0 0 0",
                            }}>Hardware & Alerts</p>

                            <ToggleRow
                                label="Camera Barcode Scanner"
                                description="Enable the camera scanner button in POS"
                                value={settings.camera_scanner_enabled}
                                onChange={() => toggle("camera_scanner_enabled")}
                            />
                            <ToggleRow
                                label="Stock & Expiry Alerts"
                                description="Show low stock and expiry warnings in the top bar"
                                value={settings.alerts_enabled}
                                onChange={() => toggle("alerts_enabled")}
                                isLast={true}
                            />
                        </div>

                        {/* ── 4. Receipt Preview ── */}
                        <div className="wh-section">
                            <p className="wh-section-title">🧾 Receipt Preview</p>
                            <p style={{ fontSize: "12px", color: "var(--text3)", marginBottom: "10px" }}>
                                Your store name, TIN, phone and address will automatically appear on all printed receipts.
                            </p>
                            <div style={{
                                padding: "14px", background: "var(--bg)",
                                border: "1px dashed var(--border2)", borderRadius: "var(--radius)",
                                fontFamily: "monospace", fontSize: "12px",
                                color: "var(--text2)", lineHeight: "1.8",
                            }}>
                                <div style={{ textAlign: "center", marginBottom: "8px" }}>
                                    <strong style={{ color: "var(--text)", fontSize: "14px" }}>
                                        {settings.store_name || "Your Store Name"}
                                    </strong><br />
                                    {settings.store_address && <span>{settings.store_address}<br /></span>}
                                    {settings.store_phone && <span>Tel: {settings.store_phone}<br /></span>}
                                    {settings.store_tin && <span>TIN: {settings.store_tin}<br /></span>}
                                </div>
                                <div style={{ borderTop: "1px dashed var(--border2)", paddingTop: "8px" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                                        <span>Subtotal</span><span>ETB 100.00</span>
                                    </div>
                                    {settings.vat_enabled === "true" && (
                                        <div style={{ display: "flex", justifyContent: "space-between", color: "var(--amber)" }}>
                                            <span>VAT ({settings.vat_rate}%)</span>
                                            <span>ETB {Number(settings.vat_rate).toFixed(2)}</span>
                                        </div>
                                    )}
                                    <div style={{
                                        display: "flex", justifyContent: "space-between",
                                        fontWeight: 700, color: "var(--text)",
                                        borderTop: "1px dashed var(--border2)",
                                        marginTop: "4px", paddingTop: "4px",
                                    }}>
                                        <span>TOTAL</span>
                                        <span>ETB {settings.vat_enabled === "true"
                                            ? (100 + Number(settings.vat_rate)).toFixed(2)
                                            : "100.00"}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                            {/* ── 5. Staff Accounts ── */}
                            <div className="wh-section" style={{ padding: 0 }}>
                                <p className="wh-section-title" style={{ padding: "16px 16px 0 16px" }}>👥 Staff Accounts</p>
                                <StaffManager
                                    currentUser={currentUser}
                                    embedded={true}
                                />
                            </div>

                        {/* ── Sign Out (mobile only — the topbar's sign-out icon can get ── */}
                        {/* ── clipped off-screen at narrow portrait widths)              ── */}
                        {onSignOut && (
                            <div className="settings-mobile-signout wh-section">
                                <p className="wh-section-title">🚪 Account</p>
                                <button
                                    className="cancel-btn"
                                    onClick={() => {
                                        onClose();
                                        onSignOut();
                                    }}
                                    style={{ width: "100%", height: "44px" }}
                                >
                                    Sign Out
                                </button>
                            </div>
                        )}

                        {/* ── Save button ── */}
                        <button
                            className="save-btn"
                            onClick={handleSave}
                            disabled={saving}
                            style={{ height: "44px", fontSize: "14px" }}
                        >
                            {saving ? "Saving..." : "Save"}
                        </button>

                    </div>
                )}
            </div>
        </div>
    );
}